import React, { useState } from 'react';

interface ChangePasswordCardProps {
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
}

// Change the logged-in user's password (requires the current password).
const ChangePasswordCard: React.FC<ChangePasswordCardProps> = ({ apiBaseUrl, getAuthHeaders }) => {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not change the password. Please try again.');
      } else {
        reset();
        setOpen(false);
        setSuccess(true);
      }
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="settings-card">
      <h3>Password</h3>

      {success && !open && <p className="settings-success">Password changed. Other devices have been signed out.</p>}

      {!open ? (
        <div className="settings-key-actions">
          <button className="secondary-button" onClick={() => { setOpen(true); setSuccess(false); }}>
            Change Password
          </button>
        </div>
      ) : (
        <div className="settings-key-form">
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setError(null); }}
              autoComplete="current-password"
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
              autoComplete="new-password"
            />
          </div>

          {error && <p className="settings-error">{error}</p>}

          <div className="settings-key-actions">
            <button
              className="primary-button"
              onClick={handleSubmit}
              disabled={busy || !currentPassword || !newPassword || !confirmPassword}
            >
              {busy ? 'Saving…' : 'Save New Password'}
            </button>
            <button className="secondary-button" onClick={() => { reset(); setOpen(false); }} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ChangePasswordCard;
